/**
 * Replaces the browser's blocking `alert()` with a toast, app-wide. The pages
 * call `alert(message)` in ~190 files; installing this once in index.tsx turns
 * every one of them into a toast without touching the pages.
 *
 * - The toast type (success / error / warning / info) is read from the message
 *   text, so "Data updated Successfully" is green and "Error Occurs during file
 *   upload" is red.
 * - `alert(response.data)` with an object shows its message / error field
 *   instead of "[object Object]".
 * - A native alert blocked until it was closed, so pages often do
 *   `alert(...); window.location.reload();`. Visible toasts are kept in
 *   sessionStorage and shown again after the reload / redirect.
 * - The same message twice in a row (double click) shows once with a "×2".
 */
import '../New_CSS/AlertToast.css';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

const DURATION_MS: Record<ToastType, number> = {
  success: 3500,
  info: 4500,
  warning: 5500,
  error: 7000,
};
const MAX_VISIBLE = 4;
const STORAGE_KEY = 'vc-alert-toasts';
// a replayed toast still gets at least this long on screen after a reload
const MIN_REPLAY_MS = 2500;

// checked in this order (see detectToastType)
const NO_ERROR_RE = /\bno errors?\b/i;
// something actually failed (server / upload / save), not a form-field prompt
const ERROR_RE = /\b(error|errors|fail(ed|ure|s)?|unable|not able|could ?n[o']t|can ?n[o']t|cannot|denied|unauthori[sz]ed|forbidden|exception|wrong|unsuccessful(ly)?|not (been )?(saved|updated|inserted|created|deleted|uploaded|sent|submitted|generated|processed))\b|try again|\boccur+(s|ed)\b/i;
// "Please select ... denied ...", "Enter only Numbers in ... approved
// employment", "End Date cannot be before ...": a form-field prompt even when
// it mentions an error / success word later on. "Please try again" /
// "Please contact admin" are not prompts.
const WARNING_LEAD_RE = /^\W*(please (?!try again|contact)|kindly|enter|select|choose|fill|upload|add|provide|check)|\bcannot be\b|\bat least\b|\bno access\b/i;
const ALREADY_RE = /\balready\b/i;
const SUCCESS_RE = /\b(success(ful(ly)?)?|suc+es+ful+y|saved|updated|inserted|created|deleted|removed|uploaded|sent|submitted|added|completed|approved|assigned|generated|extracted|copied|downloaded|shared|reloaded|thank you)\b/i;
const WARNING_RE = /\b(please|must|required|mandatory|select|choose|enter|should|exceed(s|ed)?|missing|only|not allowed|not available|not (a )?valid|invalid|valid|limit|warning|before|kindly|earlier|later|greater|lesser|duplicate|expired|session)\b/i;

const TITLES: Record<ToastType, string> = {
  success: 'Success',
  error: 'Error',
  warning: 'Attention',
  info: 'Information',
};

const ICONS: Record<ToastType, string> = {
  success: '<path d="M20 6 9 17l-5-5"/>',
  error: '<circle cx="12" cy="12" r="9"/><path d="m15 9-6 6M9 9l6 6"/>',
  warning: '<path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/><path d="M12 9v4M12 17h.01"/>',
  info: '<circle cx="12" cy="12" r="9"/><path d="M12 16v-4M12 8h.01"/>',
};

interface StoredToast {
  id: string;
  message: string;
  type: ToastType;
  until: number;
}

interface LiveToast extends StoredToast {
  el: HTMLElement;
  count: number;
  timer: number;
  remaining: number;
  startedAt: number;
}

const live: LiveToast[] = [];
let container: HTMLElement | null = null;
let nativeAlert: ((message?: any) => void) | null = null;

export const detectToastType = (message: string): ToastType => {
  if (NO_ERROR_RE.test(message)) return 'success';
  if (WARNING_LEAD_RE.test(message)) return 'warning';
  if (ERROR_RE.test(message)) return 'error';
  // "Offer Letter was already uploaded ..." is not a success
  if (ALREADY_RE.test(message)) return 'warning';
  if (SUCCESS_RE.test(message)) return 'success';
  if (WARNING_RE.test(message)) return 'warning';
  return 'info';
};

/** What a native alert would have shown, but readable for response objects. */
const toMessage = (value: any): string => {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value;
  if (value instanceof Error) return value.message;
  if (typeof value === 'object') {
    const picked = value.message || value.msg || value.error || value.errorMessage || value.statusText
      || (value.data && (value.data.message || value.data.error));
    if (typeof picked === 'string' && picked.trim()) return picked;
    try {
      return JSON.stringify(value);
    } catch (e) {
      return String(value);
    }
  }
  return String(value);
};

const readStore = (): StoredToast[] => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch (e) {
    return [];
  }
};

const writeStore = () => {
  try {
    const list: StoredToast[] = live.map(({ id, message, type, until }) => ({ id, message, type, until }));
    if (list.length) window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    else window.sessionStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // private mode / storage blocked: toasts just don't survive a reload
  }
};

const getContainer = (): HTMLElement | null => {
  if (container && document.body.contains(container)) return container;
  if (!document.body) return null;
  container = document.createElement('div');
  container.className = 'vc-toast-stack';
  container.setAttribute('aria-live', 'polite');
  container.setAttribute('aria-atomic', 'false');
  document.body.appendChild(container);
  return container;
};

const removeToast = (toast: LiveToast) => {
  window.clearTimeout(toast.timer);
  const index = live.indexOf(toast);
  if (index >= 0) live.splice(index, 1);
  writeStore();
  toast.el.classList.add('vc-toast--leaving');
  window.setTimeout(() => toast.el.remove(), 220);
};

const startTimer = (toast: LiveToast) => {
  window.clearTimeout(toast.timer);
  toast.startedAt = Date.now();
  toast.until = toast.startedAt + toast.remaining;
  toast.el.style.setProperty('--vc-toast-duration', toast.remaining + 'ms');
  toast.timer = window.setTimeout(() => removeToast(toast), toast.remaining);
  writeStore();
};

const pauseTimer = (toast: LiveToast) => {
  window.clearTimeout(toast.timer);
  toast.remaining = Math.max(1000, toast.remaining - (Date.now() - toast.startedAt));
  toast.el.classList.add('vc-toast--paused');
};

const resumeTimer = (toast: LiveToast) => {
  toast.el.classList.remove('vc-toast--paused');
  // restart the progress bar animation from the remaining time
  const bar = toast.el.querySelector('.vc-toast__progress') as HTMLElement | null;
  if (bar) {
    bar.style.animation = 'none';
    void bar.offsetWidth;
    bar.style.animation = '';
  }
  startTimer(toast);
};

const buildToast = (id: string, message: string, type: ToastType): HTMLElement => {
  const el = document.createElement('div');
  el.className = 'vc-toast vc-toast--' + type;
  el.setAttribute('role', type === 'error' || type === 'warning' ? 'alert' : 'status');
  el.dataset.id = id;

  const icon = document.createElement('span');
  icon.className = 'vc-toast__icon';
  icon.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" '
    + 'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + ICONS[type] + '</svg>';

  const body = document.createElement('div');
  body.className = 'vc-toast__body';
  const title = document.createElement('div');
  title.className = 'vc-toast__title';
  title.textContent = TITLES[type];
  const count = document.createElement('span');
  count.className = 'vc-toast__count';
  title.appendChild(count);
  const text = document.createElement('div');
  text.className = 'vc-toast__message';
  // textContent: messages come from server responses, never render them as HTML
  text.textContent = message;
  body.appendChild(title);
  body.appendChild(text);

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'vc-toast__close';
  close.setAttribute('aria-label', 'Close notification');
  close.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" '
    + 'stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>';

  const progress = document.createElement('span');
  progress.className = 'vc-toast__progress';

  el.appendChild(icon);
  el.appendChild(body);
  el.appendChild(close);
  el.appendChild(progress);
  return el;
};

const show = (message: string, type: ToastType, durationMs?: number, id?: string) => {
  const stack = getContainer();
  if (!stack) {
    // called before <body> exists: nothing to draw into yet
    if (nativeAlert) nativeAlert(message);
    return;
  }

  const same = live.find((t) => t.message === message && t.type === type);
  if (same) {
    same.count += 1;
    (same.el.querySelector('.vc-toast__count') as HTMLElement).textContent = '×' + same.count;
    same.remaining = DURATION_MS[type];
    same.el.classList.remove('vc-toast--bump');
    void same.el.offsetWidth;
    same.el.classList.add('vc-toast--bump');
    resumeTimer(same);
    return;
  }

  const toastId = id || Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
  const el = buildToast(toastId, message, type);
  const toast: LiveToast = {
    id: toastId,
    message,
    type,
    el,
    count: 1,
    timer: 0,
    remaining: durationMs || DURATION_MS[type],
    startedAt: Date.now(),
    until: 0,
  };

  el.querySelector('.vc-toast__close')!.addEventListener('click', () => removeToast(toast));
  el.addEventListener('mouseenter', () => pauseTimer(toast));
  el.addEventListener('mouseleave', () => resumeTimer(toast));
  // on touch screens a tap on the text keeps it up; swipe-free close is the X
  el.addEventListener('touchstart', () => pauseTimer(toast), { passive: true });
  el.addEventListener('touchend', () => resumeTimer(toast), { passive: true });

  live.push(toast);
  stack.appendChild(el);
  while (live.length > MAX_VISIBLE) removeToast(live[0]);
  startTimer(toast);
};

/** Shows a toast directly, for code that wants to pick the type itself. */
export const showToast = (message: any, type?: ToastType, durationMs?: number) => {
  const text = toMessage(message).trim();
  if (!text) return;
  show(text, type || detectToastType(text), durationMs);
};

const replayStored = () => {
  const now = Date.now();
  readStore()
    .filter((t) => t && t.message && t.until > now - 500)
    .forEach((t) => show(t.message, t.type, Math.max(MIN_REPLAY_MS, t.until - now), t.id));
};

export function initAlertToast(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const w = window as any;
  if (w.__vcAlertToast) return;
  w.__vcAlertToast = true;

  nativeAlert = window.alert.bind(window);
  window.alert = (message?: any) => showToast(message);

  if (document.body) replayStored();
  else document.addEventListener('DOMContentLoaded', replayStored, { once: true });
}
